import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import { averyTemplates } from "./averyTemplates";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MUIStepper from "./MUIStepper";
import LabelSheetPreview from "./LabelSheetPreview";


const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function App() {
  const [file, setFile] = useState(null);
  const [avery, setAvery] = useState("");
  const [outputFormat, setOutputFormat] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  // Preview/Sheet states
  const [showCutLines, setShowCutLines] = useState(true);

  const [zoom, setZoom] = useState(1);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setDownloadUrl(null);
    setError(null);

    setCurrentStep(2); // Move focus to Step 2
  };

  const handleGenerate = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("avery", avery);
    formData.append("file", file);

    try {
      const response = await fetch(`${API_URL}/generate`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setCurrentStep(3); // Move focus to Step 3
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fa", overflowX: 'hidden', width: '100vw', boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          mb: 4,
        }}
      >
        <Box sx={{ ml: { xs: 2, sm: 6 }, mt: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              color: "#222",
              mb: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            AI Mail Label Creator{" "}
            <Box component="span" aria-label="label" sx={{ ml: 1 }}>
              🏷️
            </Box>
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "text.secondary"}}
          >
            Save hours of tedious formatting.
            Turn messy address lists into printer-perfect labels in seconds.
          </Typography>
          <Typography variant="subtitle1"
            sx={{ color: "text.secondary" }}>
            Stop wrestling with Word templates—upload your file of names and addresses, pick an Avery
            size, download and hit print!
          </Typography>
        </Box>
      </Box>
      <Box
        component="header"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          width: "100%",
          maxWidth: 1400,
          mx: 'auto',
          background: "#fff",
          boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
          borderRadius: 3,
          mb: 4,
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 2, sm: 5 },
        }}
      >
        <MUIStepper
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          file={file}
          outputFormat={outputFormat}
          avery={avery}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            minHeight: "40vh",
          }}
        >
          {currentStep === 1 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: 450,
                mx: "auto",
                p: 3,
                bgcolor: "#fafbfc",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Upload your document
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                  fontSize: "1.1rem",
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  boxShadow: 0,
                  textTransform: "none",
                }}
                onClick={() => document.getElementById("file-input").click()}
              >
                Choose File
              </Button>
              <input
                id="file-input"
                type="file"
                accept=".txt"
                onChange={handleFileChange}
                hidden
              />
              {file && (
                <Typography
                  sx={{ mt: 2, color: "success.main", fontWeight: 500 }}
                >
                  {file.name}{" "}
                  <Box component="span" aria-label="check">
                    ✅
                  </Box>
                </Typography>
              )}
            </Box>
          )}
          {currentStep === 2 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: 900,
                mx: "auto",
                p: 3,
                bgcolor: "#fafbfc",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Choose Output Format & Label Type
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }} disabled={!file}>
                <InputLabel id="output-format-label">
                  Select file type...
                </InputLabel>
                <Select
                  labelId="output-format-label"
                  value={outputFormat}
                  label="Select file type..."
                  onChange={(e) => setOutputFormat(e.target.value)}
                >
                  <MenuItem value="pdf">PDF</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 3 }} disabled={!file}>
                <InputLabel id="avery-label">Select label format...</InputLabel>
                <Select
                  labelId="avery-label"
                  value={avery}
                  label="Select label format..."
                  onChange={(e) => setAvery(e.target.value)}
                >
                  {Object.keys(averyTemplates).map((code) => (
                    <MenuItem key={code} value={code}>
                      Avery {code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Only show preview and toggles if avery (label format) is selected */}
              {avery && (
                <>
                  <LabelSheetPreview
                    averyCode={avery}
                    labels={Array(30).fill('John Doe\n123 Main St\nCity, ST 12345')} // TODO: Replace with parsed addresses
                    showCutLines={showCutLines}
                    setShowCutLines={setShowCutLines}
                    zoom={zoom}
                    setZoom={setZoom}
                    onGenerate={() => {
                      setCurrentStep(3);
                      handleGenerate();
                    }}
                    loading={loading}
                  />
                </>
              )}
            </Box>
          )}
          {currentStep === 3 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: 450,
                mx: "auto",
                p: 3,
                bgcolor: "#fafbfc",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              {downloadUrl && !loading && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 4 }}>
                  <iframe
                    src={downloadUrl}
                    title="Label PDF Preview"
                    style={{
                      width: '100%',
                      maxWidth: 600,
                      height: 600,
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      background: '#fff',
                      boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                      marginBottom: 32
                    }}
                  />
                </Box>
              )}
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 8 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ borderRadius: 2, fontWeight: 600, px: 4, py: 1.5, fontSize: 18, boxShadow: 1 }}
                  disabled={loading || !downloadUrl}
                  startIcon={loading ? <CircularProgress size={22} color="inherit" /> : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 16V4M12 16l-4-4m4 4l4-4M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                  href={downloadUrl && !loading ? downloadUrl : undefined}
                  download={downloadUrl && !loading ? `labels_${avery}.${outputFormat}` : undefined}
                >
                  {loading ? 'Generating…' : 'Download PDF'}
                </Button>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          textAlign: "center",
          py: 4,
          color: "text.secondary",
          fontSize: "0.875rem",
          mt: 8,
        }}
      >
        © 2025 Label Creator. All rights reserved.
      </Box>
    </Box>
  );
}

export default App;
